import styled from 'styled-components';

// Helper function to get shadow style like other card components
const getShadowStyle = (theme) => {
  if (!theme?.shadow_color) return '0 4px 8px rgba(0, 0, 0, 0.1)';

  const hexToRgba = (hex, alpha = 1) => {
    const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const softShadowColor = hexToRgba(theme.shadow_color, 0.1);
  const hardShadowColor = hexToRgba(theme.shadow_color, 0.5);

  switch (theme?.card_style) {
    case 'soft-shadow-squared':
    case 'soft-shadow-slightly-rounded':
      return `0 6px 12px ${softShadowColor}`;
    case 'hard-shadow-squared':
    case 'hard-shadow-slightly-rounded':
      return `3px 3px 0 ${hardShadowColor}`;
    case 'outline-squared':
    case 'outline-slightly-rounded':
      return 'none';
    default:
      return `0 4px 8px ${softShadowColor}`;
  }
};

export const RaffleCardContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 24px;
    margin-left: auto;
    margin-right: auto;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: ${props => props.isExpired ? 'not-allowed' : 'pointer'};
    position: relative;
    opacity: ${props => props.isExpired ? 0.7 : 1};
    
    /* Apply theme-based styling with modern touches */
    box-shadow: ${({ theme }) => getShadowStyle(theme)};
    border-radius: ${({ theme }) =>
        theme?.card_style?.includes('slightly-rounded') ? '16px' :
        theme?.card_style?.includes('squared') ? '0' : '20px'};
    border: ${({ theme }) =>
        theme?.card_style?.includes('outline') ? `2px solid ${theme?.card_color || '#000'}` : 'none'};
    background-color: ${({ theme }) =>
        theme?.card_style?.includes('outline') ? 'transparent' : theme?.card_color || '#ffffff'};
    overflow: hidden;
    
    /* Modern elevation effect - disabled for expired raffles */
    &:hover {
        transform: ${props => props.isExpired ? 'none' : 'translateY(-4px)'};
        box-shadow: ${({ theme, isExpired }) => {
            if (isExpired) return getShadowStyle(theme);
            if (!theme?.shadow_color) return '0 12px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)';
            const hexToRgba = (hex, alpha = 1) => {
                const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            const shadowColor = hexToRgba(theme.shadow_color, 0.15);
            const shadowColorLight = hexToRgba(theme.shadow_color, 0.08);
            return `0 12px 24px ${shadowColor}, 0 4px 8px ${shadowColorLight}`;
        }};
    }
    
    /* Add "ENDED" overlay for expired raffles */
    ${props => props.isExpired && `
        &::after {
            content: "ENDED";
            position: absolute;
            top: 20px;
            right: 20px;
            background: rgba(220, 53, 69, 0.9);
            color: white;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 700;
            z-index: 10;
            backdrop-filter: blur(4px);
        }
    `}
`;

export const RaffleImageSection = styled.div`
    aspect-ratio: 16/9; /* Changed from 4/3 to 16/9 for featured image */
    position: relative;
    padding: 16px;
    overflow: hidden; /* Ensure image stays within bounds */
    border-radius: ${({ theme }) =>
        theme?.card_style?.includes('slightly-rounded') ? '16px 16px 0 0' :
        theme?.card_style?.includes('squared') ? '0' : '20px 20px 0 0'};
    
    .raffle-thumbnail {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        position: relative;
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '12px' :
            theme?.card_style?.includes('squared') ? '8px' : '16px'};
    }
    
    /* Dark overlay for raffle images - positioned to be flush with image bottom */
    .image-overlay {
        position: absolute;
        bottom: 16px; /* Match the container padding to align with image bottom */
        left: 16px;   /* Match the container padding to align with image left */
        right: 16px;  /* Match the container padding to align with image right */
        height: 25%; /* Height of the overlay */
        background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
        z-index: 1;
        pointer-events: none;
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '0 0 12px 12px' :
            theme?.card_style?.includes('squared') ? '0 0 8px 8px' : '0 0 16px 16px'};
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    /* Subtle hover effect for thumbnail and overlay together */
    &:hover .raffle-thumbnail {
        transform: scale(1.02);
    }
    
    &:hover .image-overlay {
        transform: scale(1.02);
    }
    
    /* Enhanced fallback design with raffle theme */
    .raffle-hero-bg {
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 25%, #feca57 50%, #ff9ff3 75%, #54a0ff 100%);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '12px' :
            theme?.card_style?.includes('squared') ? '8px' : '16px'};
    }
    
    .gradient-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, 
            rgba(255, 107, 107, 0.9) 0%, 
            rgba(238, 90, 36, 0.85) 25%, 
            rgba(254, 202, 87, 0.8) 50%,
            rgba(255, 159, 243, 0.85) 75%,
            rgba(84, 160, 255, 0.9) 100%
        );
        border-radius: ${({ theme }) =>
            theme?.card_style?.includes('slightly-rounded') ? '12px' :
            theme?.card_style?.includes('squared') ? '8px' : '16px'};
    }
    
    .raffle-icon {
        position: relative;
        z-index: 2;
        width: 72px;
        height: 72px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        color: white;
        backdrop-filter: blur(12px);
        border: 3px solid rgba(255, 255, 255, 0.2);
        transition: all 0.3s ease;
    }
    
    &:hover .raffle-icon {
        transform: scale(1.1);
        background: rgba(255, 255, 255, 0.25);
    }
`;

export const RaffleTitle = styled.h3`
    position: absolute;
    bottom: 32px;
    left: 32px;
    right: 32px;
    z-index: 2;
    color: white;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    line-height: 1.3;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
    letter-spacing: -0.01em;
`;

export const RaffleContent = styled.div`
    padding: 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const RaffleStatsRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

export const InfoBox = styled.div`
    background: ${props => 
        props.theme?.card_style?.includes('outline') ? 'transparent' : props.theme?.card_color || '#ffffff'};
    border: 1px solid ${props => props.theme?.card_text_color || '#e5e7eb'}20;
    border-radius: 8px;
    padding: 12px 16px;
    flex: 1;
    min-width: 120px;
    
    .label {
        font-size: 12px;
        font-weight: 500;
        color: ${props => props.theme?.card_text_color || '#6b7280'};
        margin-bottom: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .value {
        font-size: 16px;
        font-weight: 700;
        color: ${props => props.theme?.card_text_color || '#111827'};
        
        &.countdown {
            color: ${props => props.isExpired ? '#dc2626' : '#059669'};
        }
    }
`;

export const StatPill = styled.div`
    background: ${props => {
        if (props.theme?.card_style?.includes('outline')) {
            return 'transparent';
        }
        return props.theme?.card_text_color ? `${props.theme.card_text_color}12` : '#f8f9fa';
    }};
    color: ${props => props.theme?.card_text_color || '#64748b'};
    border: ${props => props.theme?.card_style?.includes('outline') ? `1px solid ${props.theme?.card_text_color || '#64748b'}` : 'none'};
    padding: 8px 14px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: all 0.2s ease;
    margin-bottom: 8px;
    
    svg {
        font-size: 11px;
        opacity: 0.8;
    }
    
    &:hover {
        transform: translateY(-1px);
        background: ${props => {
            if (props.theme?.card_style?.includes('outline')) {
                return props.theme?.card_text_color ? `${props.theme.card_text_color}08` : '#f1f5f9';
            }
            return props.theme?.card_text_color ? `${props.theme.card_text_color}18` : '#e2e8f0';
        }};
    }
`;

export const StatsGroup = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

export const PricePill = styled.div`
    background: ${props => {
        if (props.theme?.card_style?.includes('outline')) {
            return 'transparent';
        }
        return props.theme?.card_text_color ? `${props.theme.card_text_color}12` : '#f8f9fa';
    }};
    color: ${props => props.theme?.card_text_color || '#64748b'};
    border: ${props => props.theme?.card_style?.includes('outline') ? `1px solid ${props.theme?.card_text_color || '#64748b'}` : 'none'};
    padding: 8px 14px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: all 0.2s ease;
    
    svg {
        font-size: 11px;
        opacity: 0.8;
    }
    
    &:hover {
        transform: translateY(-1px);
        background: ${props => {
            if (props.theme?.card_style?.includes('outline')) {
                return props.theme?.card_text_color ? `${props.theme.card_text_color}08` : '#f1f5f9';
            }
            return props.theme?.card_text_color ? `${props.theme.card_text_color}18` : '#e2e8f0';
        }};
    }
`;

export const CountdownPill = styled.div`
    background: ${props => {
        if (props.isExpired) {
            return '#dc3545'; // Red for expired
        }
        if (props.theme?.card_style?.includes('outline')) {
            return 'transparent';
        }
        return '#28a745'; // Green for active countdown
    }};
    color: ${props => {
        if (props.isExpired) {
            return 'white';
        }
        if (props.theme?.card_style?.includes('outline')) {
            return '#28a745';
        }
        return 'white';
    }};
    border: ${props => {
        if (props.theme?.card_style?.includes('outline')) {
            return props.isExpired ? '1px solid #dc3545' : '1px solid #28a745';
        }
        return 'none';
    }};
    padding: 8px 14px;
    border-radius: 24px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: all 0.2s ease;
    margin-bottom: 8px;
    
    svg {
        font-size: 11px;
        opacity: 0.9;
    }
    
    &:hover {
        transform: translateY(-1px);
        background: ${props => {
            if (props.isExpired) {
                return '#c82333'; // Darker red on hover
            }
            if (props.theme?.card_style?.includes('outline')) {
                return props.isExpired ? '#dc354508' : '#28a74508';
            }
            return '#218838'; // Darker green on hover
        }};
    }
`;

export const EnterRaffleButton = styled.button`
    width: 100%;
    min-height: 52px;
    font-size: 16px;
    font-weight: 700;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: auto;
    letter-spacing: -0.01em;
    opacity: ${props => props.disabled ? 0.6 : 1};
    outline: none;
    
    /* Apply theme-based button styling with modern touches */
    border-radius: ${({ theme }) => {
        if (theme?.button_style?.includes('completely-rounded')) return '26px';
        if (theme?.button_style?.includes('slightly-rounded')) return '12px';
        return '8px'; // Default rounded for raffles
    }};
    
    background-color: ${({ theme, disabled }) => {
        if (disabled) return '#6c757d'; // Gray for disabled
        if (theme?.button_style?.includes('outline')) return 'transparent';
        return theme?.button_color || '#3b82f6'; // Blue default for raffles
    }};
    
    border: ${({ theme, disabled }) => {
        if (theme?.button_style?.includes('outline')) {
            const color = disabled ? '#6c757d' : theme?.button_color || '#3b82f6';
            return `2px solid ${color}`;
        }
        return 'none';
    }};
    
    color: ${({ theme, disabled }) => {
        if (disabled) return 'white';
        if (theme?.button_style?.includes('outline')) {
            return theme?.button_color || '#3b82f6';
        }
        return theme?.button_text_color || 'white';
    }};
    
    /* No shadow by default - clean flat design */
    box-shadow: none;
    
    &:hover:not(:disabled) {
        background-color: ${({ theme }) => {
            if (theme?.button_style?.includes('outline')) {
                return theme?.button_color || '#3b82f6';
            }
            const buttonColor = theme?.button_color || '#3b82f6';
            return buttonColor === '#3b82f6' ? '#2563eb' : buttonColor;
        }};
        
        color: ${({ theme }) => {
            if (theme?.button_style?.includes('outline')) {
                return theme?.button_text_color || 'white';
            }
            return theme?.button_text_color || 'white';
        }};
        
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    }
    
    &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: none;
    }
    
    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
    }
`;
